import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { Eye, EyeOff } from "lucide-react";
import { useSession } from "../context/SessionContext";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

const Signup = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const { session } = useSession();

  useEffect(() => {
    if (session) {
      navigate("/");
    }
  }, [session, navigate]);

  useEffect(() => {
    const savedLang = localStorage.getItem("preferredLanguage") || "es";
    i18n.changeLanguage(savedLang).catch((err) =>
      console.error("Error applying language from localStorage:", err)
    );
  }, [i18n]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t("passwords_do_not_match"));
      return;
    }

    if (!/^(?=.*[A-Z])(?=.*\d).{8,}$/.test(password)) {
      toast.error(t("password_requirements"));
      return;
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (error) {
      toast.error(error.message);
      return;
    }

    toast.success(t("signup_success_check_email"));
    
    setTimeout(() => navigate("/login"), 3000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-14">
          <img
            src="/logo.svg"
            alt="Omniloy Logo"
            className="w-12 h-12 mx-auto mb-4"
          />
        </div>

        <div>
          <div className="text-left mb-6">
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              {t("create_account")}
            </h1>
            <p className="text-sm text-gray-700">{t("signup_subtitle")}</p>
          </div>

          <form onSubmit={handleSignup} className="space-y-6">
            <div>
              <label
                htmlFor="email"
                className="block text-lg font-bold text-black mb-2"
              >
                {t("emailLabel")}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 bg-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                placeholder={t("emailPlaceholder")}
                required
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-lg font-bold text-black mb-2"
              >
                {t("passwordLabel")}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border bg-white border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder={t("passwordPlaceholder")}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-lg font-bold text-black mb-2"
              >
                {t("confirm_password")}
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border bg-white border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder={t("confirm_new_password")}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="text-xs text-gray-600 bg-blue-50 p-3 rounded-md border border-blue-100">
              {t("password_must_contain")}:
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>{t("at_least_8_characters")}</li>
                <li>{t("one_uppercase_letter")}</li>
                <li>{t("one_number")}</li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={!email || !password || !confirmPassword}
              className="w-full bg-[#105BDB] hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-md transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
            >
              {t("signup_button")}
            </button>

            <div className="text-center">
              <span className="text-sm text-gray-700">{t("already_have_account")} </span>
              <Link
                to="/login"
                className="text-sm text-[#105BDB] hover:underline"
              >
                {t("login_link")}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;